import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import Button from '../../../../components/Button';
import Checkbox from '../../../../components/Checkbox';
import Dropdown from '../../../../components/Dropdown';
import { useConsultation } from '../../../../context/ConsultationContext';
import { handleTreatmentServiceNavigation } from '../components/treatmentNavigationHelper';
import styles from './css/BloodPressure.module.css';
import newStyle from './css/Injection.module.css';
import { Syringe, Send } from 'lucide-react';

const Injection = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { consultationData, updateConsultationData, getStepDraft, setStepDraft, goToStep } = useConsultation();

    const patient = location?.state?.patient || consultationData?.patient || {};
    const patientName = `${patient?.firstname || ""} ${patient?.surname || ""}`.trim() || "Thandiwe Ndlovu";
    const profileNumber = patient?.profilenumber || "448896";
    const initials = `${patient?.firstname?.[0] || ""}${patient?.surname?.[0] || ""}`.toUpperCase() || "TN";

    // Injection details states
    const [medicationName, setMedicationName] = useState('');
    const [dose, setDose] = useState('');
    const [route, setRoute] = useState('');
    const [site, setSite] = useState('');

    // Other states
    const [clinicalNotes, setClinicalNotes] = useState('');

    const [adverseReactions, setAdverseReactions] = useState({
        adverseReactionObserved: false,
        patientMonitored: false,
        none: false
    });

    const handleAdverseReactionChange = (reaction) => {
        setAdverseReactions(prev => ({
            ...prev,
            [reaction]: !prev[reaction]
        }));
    };

    const handleInvoice = () => {
        handleTreatmentServiceNavigation({
            location,
            navigate,
            consultationData,
            updateConsultationData,
            getStepDraft,
            setStepDraft,
            goToStep,
            serviceData: {
                injection: {
                    medicationName,
                    dose,
                    route,
                    site,
                    clinicalNotes,
                    adverseReactions,
                },
            },
        });
    };

    useEffect(() => {
        document.body.classList.add(styles.hideSidebar);
        return () => {
            document.body.classList.remove(styles.hideSidebar);
        };
    }, []);

    return (
        <div className={styles.bloodPressureContainer}>
            <Navbar />

            <main className={styles.mainContent}>
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <div className={styles.headerIcon} aria-hidden="true">
                            <Syringe />
                        </div>
                        <h1 className={newStyle.title}>Injection Administration</h1>
                    </div>

                    <div className={styles.patientCard}>
                        <div className={styles.avatar} aria-hidden="true">{initials}</div>
                        <div className={styles.patientText}>
                            <div className={styles.patientName}>{patientName}</div>
                            <div className={styles.patientMetaRow}>
                                <span className={styles.profileInfo}>Profile: {profileNumber}</span>
                                <span className={styles.dependentBadge}>Dependent</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.sectionTitle}>INJECTION DETAILS</div>
                        <div className={newStyle.readingsRow}>
                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Medication Name</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="text"
                                        value={medicationName}
                                        onChange={(e) => setMedicationName(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='e.g., Vitamin B12'
                                    />
                                </div>
                            </div>

                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Dose</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="text"
                                        value={dose}
                                        onChange={(e) => setDose(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='e.g., 1mg/1ml'
                                    />
                                </div>
                            </div>

                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Route</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="text"
                                        value={route}
                                        onChange={(e) => setRoute(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='e.g., Intramuscular'
                                    />
                                </div>
                            </div>

                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Site</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="text"
                                        value={site}
                                        onChange={(e) => setSite(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='e.g., Left deltoid'
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.sectionTitle}>ADVERSE REACTIONS</div>

                        <div className={styles.symptomsList}>
                            <div className={styles.checkboxItem}>
                                <Checkbox
                                    id="adverseReactionObserved"
                                    checked={adverseReactions.adverseReactionObserved}
                                    onChange={() => handleAdverseReactionChange('adverseReactionObserved')}
                                />
                                <label htmlFor="adverseReactionObserved" className={styles.checkboxLabel}>Adverse reaction observed</label>
                            </div>

                            <div className={styles.checkboxItem}>
                                <Checkbox
                                    id="patientMonitored"
                                    checked={adverseReactions.patientMonitored}
                                    onChange={() => handleAdverseReactionChange('patientMonitored')}
                                />
                                <label htmlFor="patientMonitored" className={styles.checkboxLabel}>Patient monitored for 15 min post-injection</label>
                            </div>

                            <div className={styles.checkboxItem}>
                                <Checkbox
                                    id="none"
                                    checked={adverseReactions.none}
                                    onChange={() => handleAdverseReactionChange('none')}
                                />
                                <label htmlFor="none" className={styles.checkboxLabel}>None</label>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.fieldLabel}>Clinical Notes</div>
                        <textarea
                            value={clinicalNotes}
                            onChange={(e) => setClinicalNotes(e.target.value)}
                            className={styles.clinicalNotes}
                            placeholder="Enter clinical notes..."
                            rows={6}
                        />
                    </div>

                    <div className={styles.cardFooter}>
                        <Button
                            onClick={handleInvoice}
                            className={styles.invoiceButton}
                            type="button"
                        >
                            <Send size={16} />
                            Invoice
                        </Button>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default Injection;